/**
 * TipTap HTML ↔ Markdown for legal docs (Flutter flutter_markdown).
 * Kept dependency-free so the same logic can live in the SPA and edge functions.
 */

function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, " ")
    .replace(/&#x27;|&apos;/gi, "'")
    .replace(/&#39;/g, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&#x2F;/gi, "/")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&amp;/gi, "&");
}

function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

/** Convert TipTap / HTML body to Markdown for Firebase + Flutter. */
export function htmlToMarkdown(input: string | null | undefined): string {
  if (input == null) return "";
  let html = input.trim();
  if (html === "") return "";

  if (!looksLikeHtml(html)) {
    return html.replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  }

  html = html
    .replace(/\r\n/g, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<p[^>]*>/gi, "")
    .replace(/<\/div>/gi, "\n\n")
    .replace(/<div[^>]*>/gi, "")
    .replace(/<\/h1>/gi, "\n\n")
    .replace(/<h1[^>]*>/gi, "# ")
    .replace(/<\/h2>/gi, "\n\n")
    .replace(/<h2[^>]*>/gi, "## ")
    .replace(/<\/h3>/gi, "\n\n")
    .replace(/<h3[^>]*>/gi, "### ")
    .replace(/<\/h4>/gi, "\n\n")
    .replace(/<h4[^>]*>/gi, "#### ")
    .replace(/<\/blockquote>/gi, "\n\n")
    .replace(/<blockquote[^>]*>/gi, "> ")
    .replace(/<\/li>/gi, "\n")
    .replace(/<li[^>]*>/gi, "- ")
    .replace(/<\/?(?:ul|ol)[^>]*>/gi, "\n")
    .replace(/<(strong|b)(?:\s[^>]*)?>/gi, "**")
    .replace(/<\/(strong|b)>/gi, "**")
    .replace(/<(em|i)(?:\s[^>]*)?>/gi, "*")
    .replace(/<\/(em|i)>/gi, "*")
    .replace(/<u(?:\s[^>]*)?>/gi, "")
    .replace(/<\/u>/gi, "")
    .replace(/<a[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi, "[$2]($1)")
    .replace(/<\/?[a-z][^>]*>/gi, "");

  return decodeHtmlEntities(html)
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Convert Markdown / plain text to TipTap-friendly HTML. */
export function markdownToHtml(input: string | null | undefined): string {
  if (input == null) return "";
  const text = input.replace(/\r\n/g, "\n").trim();
  if (text === "") return "";

  if (looksLikeHtml(text)) {
    return text;
  }

  const escape = (value: string) =>
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const inline = (value: string) => {
    let out = escape(value);
    out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    out = out.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
    out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
    return out.replace(/\n/g, "<br>");
  };

  const blocks = text.split(/\n\s*\n+/);
  return blocks
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return "";

      if (/^###\s+/.test(trimmed)) {
        return `<h3>${inline(trimmed.replace(/^###\s+/, ""))}</h3>`;
      }
      if (/^##\s+/.test(trimmed)) {
        return `<h2>${inline(trimmed.replace(/^##\s+/, ""))}</h2>`;
      }
      if (/^#\s+/.test(trimmed)) {
        return `<h2>${inline(trimmed.replace(/^#\s+/, ""))}</h2>`;
      }
      if (/^>\s?/.test(trimmed)) {
        const quote = trimmed
          .split("\n")
          .map((line) => line.replace(/^>\s?/, ""))
          .join("\n");
        return `<blockquote><p>${inline(quote)}</p></blockquote>`;
      }
      if (/^[-*]\s+/m.test(trimmed) && trimmed.split("\n").every((l) => /^[-*]\s+/.test(l.trim()) || l.trim() === "")) {
        const items = trimmed
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => `<li>${inline(line.replace(/^[-*]\s+/, ""))}</li>`)
          .join("");
        return `<ul>${items}</ul>`;
      }

      return `<p>${inline(trimmed)}</p>`;
    })
    .filter(Boolean)
    .join("");
}
